import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Loader, Center, Stack, Text } from '@mantine/core';

/**
 * MineMinerAdminRoute - Route guard for MineMiner internal admins only (MineMinerAdmin role).
 *
 * Narrower than MineMinerRoute, which also admits MineMinerRep. Client, organization and
 * Sync-side users never hold MineMinerAdmin, so they are refused too.
 *
 * Redirects:
 * - Unauthenticated users to /login, carrying the requested path as `returnTo`
 * - Authenticated users without MineMinerAdmin to /access-denied
 *
 * Like every guard here, it decides which screen is shown and grants no data access.
 */
export const MineMinerAdminRoute: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isMineMinerAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (!isMineMinerAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  return <Outlet />;
};
